import { AlertCircle, CheckCircle, CreditCard, Info, Mail, MapPin, Phone, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import inseamHelpImage from '../assets/images/inseam-logo.jpg';
import { API_ENDPOINTS, buildApiUrl } from '../config/api';
import { useBooking } from '../contexts/BookingContext';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/customer-info-light.css';
import { notifySupportApiNetworkError, notifySupportApiServerError } from '../utils/supportApiErrorNotifier';
import StepContainer from './StepContainer';

const CustomerInfo = ({ onInfoSubmit, bookingData }) => {
  const [formData, setFormData] = useState({
    name: bookingData.customerInfo?.name || '',
    email: bookingData.customerInfo?.email || '',
    phone: bookingData.customerInfo?.phone || '',
    phonePrefix: bookingData.customerInfo?.phonePrefix || '+34',
    dni: bookingData.customerInfo?.dni || '',
    address: bookingData.customerInfo?.address || '',
  });
  const requiresAddress = (bookingData.pricing?.grandTotal ?? 0) >= 3000;
  const eligibleInseamVehicles = (bookingData.selectedVehicles || []).filter(
    (v) => v && v.inseamOptionalEnabled
  );
  const [inseamByVehicleId, setInseamByVehicleId] = useState(
    bookingData.inseamCmByVehicleId || {}
  );

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInseamHelp, setShowInseamHelp] = useState(false);
  const inseamHelpWrapRef = useRef(null);
  const { clearTemporaryBooking, updateBookingData } = useBooking();
  const { t, language } = useTranslation();

  // El backend recalcula el total de la reserva de forma independiente (nunca confía en el
  // importe enviado por el frontend). Tras crear/actualizar la reserva temporal, sincronizamos
  // aquí los totales mostrados en el resumen/checkout con los realmente persistidos en el
  // backend, para evitar que el importe mostrado difiera del que luego exige Stripe.
  const syncPricingWithBookingTotals = (booking) => {
    if (!booking) return;
    const vehiclesTotal = Number(booking.vehiclesTotal);
    const accessoriesTotal = Number(booking.accessoriesTotal);
    const insuranceTotal = Number(booking.insuranceTotal);
    const deliveryCost = Number(booking.deliveryCost);
    const grandTotal = Number(booking.total);
    if (![vehiclesTotal, accessoriesTotal, insuranceTotal, deliveryCost, grandTotal].every(Number.isFinite)) {
      return;
    }
    updateBookingData({
      pricing: {
        ...bookingData.pricing,
        vehicles: { ...bookingData.pricing?.vehicles, total: vehiclesTotal },
        accessories: {
          ...bookingData.pricing?.accessories,
          total: accessoriesTotal,
          insuranceTotal,
          accessoriesOnlyTotal: Math.max(0, Math.round((accessoriesTotal - insuranceTotal) * 100) / 100),
        },
        delivery: { ...bookingData.pricing?.delivery, cost: deliveryCost },
        grandTotal,
      },
    });
  };

  useEffect(() => {
    if (!showInseamHelp) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowInseamHelp(false);
    };
    const onPointerDown = (e) => {
      const el = inseamHelpWrapRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      setShowInseamHelp(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [showInseamHelp]);

  // Sincronizar el estado local con las props cuando cambien
  useEffect(() => {
    if (bookingData.customerInfo) {
      setFormData({
        name: bookingData.customerInfo.name || '',
        email: bookingData.customerInfo.email || '',
        phone: bookingData.customerInfo.phone || '',
        phonePrefix: bookingData.customerInfo.phonePrefix || '+34',
        dni: bookingData.customerInfo.dni || '',
        address: bookingData.customerInfo.address || '',
      });
    }
  }, [bookingData.customerInfo]);

  useEffect(() => {
    setInseamByVehicleId(bookingData.inseamCmByVehicleId || {});
  }, [bookingData.inseamCmByVehicleId]);

  // API_BASE_URL ya no es necesario, usamos buildApiUrl

  // Validar un solo campo (para onBlur). Devuelve mensaje de error o null.
  const validateField = (fieldName, value) => {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    switch (fieldName) {
      case 'name': {
        if (!trimmed) return t('customer.name.required');
        if (trimmed.length < 2) return t('customer.name.min.length');
        if (trimmed.length > 100) return t('customer.name.max.length');
        // Solo letras (incl. acentuadas), espacios, guión y apóstrofe
        if (!/^[\p{L}\s'-]+$/u.test(trimmed)) return t('customer.name.invalid');
        return null;
      }
      case 'email': {
        if (!trimmed) return t('customer.email.required');
        // Formato correo: local@dominio.ext
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return t('customer.email.invalid');
        if (trimmed.length > 254) return t('customer.email.max.length');
        return null;
      }
      case 'phone':
        if (!trimmed) return t('customer.phone.required');
        if (!/^[0-9]{9}$/.test(trimmed)) return t('customer.phone.invalid');
        return null;
      case 'phonePrefix': {
        if (!trimmed) return t('customer.phone.prefix.required');
        if (!/^\+?[1-9]\d{0,3}$/.test(trimmed)) return t('customer.phone.prefix.invalid');
        return null;
      }
      case 'dni': {
        const docTrimmed = trimmed.toUpperCase();
        if (!docTrimmed) return t('customer.dni.required');
        const dniRegex = /^[0-9]{8}[A-Z]$/;
        const nieRegex = /^[XYZ][0-9]{7}[A-Z]$/;
        const passportRegex = /^[A-Z0-9]{5,20}$/;
        if (!dniRegex.test(docTrimmed) && !nieRegex.test(docTrimmed) && !passportRegex.test(docTrimmed)) {
          return t('customer.dni.invalid');
        }
        return null;
      }
      case 'address':
        if (requiresAddress && !trimmed) return t('customer.address.required');
        return null;
      default:
        return null;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = ['name', 'email', 'phone', 'phonePrefix', 'dni'];
    if (requiresAddress) fieldsToValidate.push('address');
    fieldsToValidate.forEach((field) => {
      const value = field === 'phonePrefix' ? formData.phonePrefix : field === 'dni' ? formData.dni : field === 'address' ? formData.address : formData[field];
      const err = validateField(field, value);
      if (err) newErrors[field] = err;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Normalizar prefijo: guardar con '+' delante si no lo tiene
  const normalizePhonePrefix = (prefix) => {
    if (!prefix || !prefix.trim()) return '+34';
    let p = prefix.trim();
    if (p.startsWith('00')) p = `+${p.slice(2)}`;
    return p.startsWith('+') ? p : `+${p}`;
  };

  // Normalizar documento: mismo formato (mayúsculas, sin espacios) que se usa para validarlo
  const normalizeDni = (dni) => (typeof dni === 'string' ? dni.trim().toUpperCase() : dni);

  const parseInseamCmOrNull = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return NaN;
    if (n < 30 || n > 120) return NaN;
    return Math.round(n * 10) / 10;
  };

  const buildInseamPayload = () => {
    if (!eligibleInseamVehicles.length) return undefined;
    const payload = {};
    for (const v of eligibleInseamVehicles) {
      const raw = inseamByVehicleId?.[v.id];
      const parsed = parseInseamCmOrNull(raw);
      if (Number.isNaN(parsed)) {
        throw new Error(t('inseam.error.invalid'));
      }
      payload[String(v.id)] = parsed;
    }
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      setApiError('');
      const phonePrefixToSave = normalizePhonePrefix(formData.phonePrefix);
      const dniToSave = normalizeDni(formData.dni);

      try {
        const inseamPayload = buildInseamPayload();

        // Si ya existe una reserva temporal, la reutilizamos
        if (bookingData.bookingId && bookingData.temporaryBlockIds?.length > 0) {
          
          // Solo actualizamos la información del cliente si ha cambiado
          const customerInfoChanged = 
            formData.name !== bookingData.customerInfo?.name ||
            formData.email !== bookingData.customerInfo?.email ||
            formData.phone !== bookingData.customerInfo?.phone ||
            phonePrefixToSave !== (bookingData.customerInfo?.phonePrefix || normalizePhonePrefix(bookingData.customerInfo?.phonePrefix)) ||
            dniToSave !== bookingData.customerInfo?.dni ||
            (requiresAddress && (formData.address !== (bookingData.customerInfo?.address ?? ''))) ||
            JSON.stringify(inseamPayload || {}) !== JSON.stringify(bookingData.inseamCmByVehicleId || {});
          
          if (customerInfoChanged) {
            // Actualizar información del cliente en la reserva existente
            const updatePayload = {
              customerEmail: formData.email,
              customerName: formData.name,
              customerPhone: formData.phone,
              customerPhonePrefix: phonePrefixToSave,
              customerDni: dniToSave,
              language,
              ...(inseamPayload ? { inseamCmByVehicleId: inseamPayload } : {}),
            };
            if (requiresAddress) updatePayload.customerAddress = formData.address?.trim() || '';
            const updateResponse = await fetch(buildApiUrl(`${API_ENDPOINTS.BOOKING_TEMP}/${bookingData.bookingId}`), {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(updatePayload)
            });

            if (!updateResponse.ok) {
              notifySupportApiServerError(updateResponse.status);
              const errorData = await updateResponse.json();
              throw new Error(errorData.message || t('customer.error.update.temp'));
            }

            const updateResponseData = await updateResponse.json();
            syncPricingWithBookingTotals(updateResponseData.booking);
          }
          
          // Continuamos con la reserva existente
          onInfoSubmit({
            customerInfo: {
              ...formData,
              phonePrefix: phonePrefixToSave,
              dni: dniToSave,
              address: formData.address?.trim() || '',
            },
            inseamCmByVehicleId: inseamPayload || {},
            temporaryBlockIds: bookingData.temporaryBlockIds,
            bookingId: bookingData.bookingId
          });
          return;
        }

        // Si no existe una reserva temporal, creamos una nueva
        // Verificar que tenemos todos los datos necesarios
        if (!bookingData.selectedVehicles || bookingData.selectedVehicles.length === 0) {
          throw new Error(t('customer.error.no.vehicles'));
        }
        
        if (!bookingData.dates?.start || !bookingData.dates?.end) {
          throw new Error(t('customer.error.no.dates'));
        }
        
        // Usar el pricing calculado del estado
        const total = bookingData.pricing.grandTotal;
        const vehiclesTotal = bookingData.pricing.vehicles.total;
        const accessoriesTotal = bookingData.pricing.accessories.total;
        const insuranceTotal = bookingData.pricing.accessories.insuranceTotal ?? 0;
        const deliveryCost = bookingData.pricing.delivery.cost;
        const deliveryType = bookingData.deliveryOption?.type === 'delivery' ? 'DELIVERY' : 'PICKUP';

        const accessoryLines = (bookingData.pricing?.accessories?.items || [])
          .filter(
            (item) =>
              item &&
              item.vehicleId != null &&
              item.id != null &&
              typeof item.totalPrice === 'number'
          )
          .map((item) => ({
            vehicleId: item.vehicleId,
            accessoryId: item.id,
            price: item.totalPrice
          }));

        // Preparar los datos para la reserva temporal
        const tempBookingData = {
          customerEmail: formData.email,
          customerName: formData.name,
          customerPhone: formData.phone,
          customerPhonePrefix: phonePrefixToSave,
          customerDni: dniToSave,
          language,
          vehicles: bookingData.selectedVehicles.map(vehicle => vehicle.id),
          ...(inseamPayload ? { inseamCmByVehicleId: inseamPayload } : {}),
          startDateTime: bookingData.dates.start,
          endDateTime: bookingData.dates.end,
          deliveryType: deliveryType,
          deliveryCost: deliveryCost,
          vehiclesTotal: vehiclesTotal,
          accessoriesTotal: accessoriesTotal,
          insuranceTotal: insuranceTotal,
          total: total,
          ...(accessoryLines.length > 0 ? { accessories: accessoryLines } : {}),
          ...(deliveryType === 'DELIVERY' && bookingData.deliveryOption?.address
            ? { hotelAddress: String(bookingData.deliveryOption.address).trim() }
            : {})
        };
        if (requiresAddress) tempBookingData.customerAddress = formData.address?.trim() || '';

        // Realizar la llamada al endpoint
        const response = await fetch(buildApiUrl(API_ENDPOINTS.BOOKING_TEMP), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(tempBookingData)
        });

        if (!response.ok) {
          notifySupportApiServerError(response.status);
          const errorData = await response.json();
          
          // Si el error es que los vehículos no están disponibles, sugerir limpiar la reserva
          if (errorData.message && errorData.message.includes('no están disponibles')) {
            throw new Error(`${errorData.message}. ${t('customer.error.vehicles.unavailable.help')}`);
          }
          
          throw new Error(errorData.message || t('customer.error.create.temp'));
        }

        const responseData = await response.json();
        syncPricingWithBookingTotals(responseData.booking);

        // Si todo va bien, continuamos con el flujo normal
        onInfoSubmit({
          customerInfo: {
            ...formData,
            phonePrefix: phonePrefixToSave,
            dni: dniToSave,
            address: formData.address?.trim() || '',
          },
          inseamCmByVehicleId: inseamPayload || {},
          temporaryBlockIds: responseData.temporaryBlockIds,
          bookingId: responseData.booking.id
        });
      } catch (error) {
        notifySupportApiNetworkError(error);
        setApiError(error.message || t('customer.error.handle.temp'));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // El '00' de marcación internacional equivale a '+' (ej. 0034 -> +34)
    const nextValue = name === 'phonePrefix' && value.trimStart().startsWith('00')
      ? `+${value.trimStart().slice(2)}`
      : value;
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    const keysToClear = name === 'phone' || name === 'phonePrefix' ? ['phone', 'phonePrefix'] : [name];
    setErrors(prev => {
      const next = { ...prev };
      keysToClear.forEach(k => { if (next[k]) next[k] = ''; });
      return next;
    });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const err = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: err || '' }));
  };

  return (
    <div className="customer-info-light">
      <div className="max-w-4xl mx-auto px-4 py-5 sm:p-6">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold text-2c3e50 font-orbitron mb-2">{t('customer.title')}</h2>
      </div>

      <div className="mb-5 sm:mb-6">
        <p className="text-6c757d text-center text-sm sm:text-base">
          {t('customer.subtitle')}
        </p>
      </div>

      {apiError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
          <div className="flex gap-2 items-center text-red-500 mb-3">
            <AlertCircle size={20} />
            <span>{apiError}</span>
          </div>
          {(apiError.includes('no están disponibles') || apiError.includes('not available') || apiError.includes('nicht verfügbar')) && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  clearTemporaryBooking();
                  setApiError('');
                }}
                className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors"
              >
                {t('customer.clear.temp.booking')}
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 transition-colors"
              >
                {t('customer.reload.page')}
              </button>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
        {/* Nombre completo */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-2c3e50">
            <User size={16} className="text-6c757d" />
            <span>{t('customer.name.label')}</span>
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
                className={`w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-sm text-2c3e50 outline-none transition-all duration-200 focus:ring-1 focus:ring-EBC122 ${
                errors.name ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-EBC122'
            }`}
            placeholder={t('customer.name.placeholder')}
            maxLength={100}
            required
          />
          {errors.name && (
            <div className="flex gap-2 items-center text-red-500 text-sm">
              <AlertCircle size={14} />
              <span>{errors.name}</span>
            </div>
          )}
        </div>

        {/* Email y Teléfono en grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-2c3e50">
              <Mail size={16} className="text-6c757d" />
              <span>{t('customer.email.label')}</span>
              <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
                className={`w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-sm text-2c3e50 outline-none transition-all duration-200 focus:ring-1 focus:ring-EBC122 ${
                errors.email ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-EBC122'
              }`}
              placeholder={t('customer.email.placeholder')}
              maxLength={254}
              required
            />
            {errors.email && (
              <div className="flex gap-2 items-center text-red-500 text-sm">
                <AlertCircle size={14} />
                <span>{errors.email}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex gap-3 sm:gap-4">
              <div className="space-y-2 w-24 sm:w-28">
                <label className="flex items-center gap-2 text-sm font-medium text-2c3e50" htmlFor="phonePrefix">
                  <Phone size={16} className="text-6c757d" />
                  <span>{t('customer.phone.prefix.label')}</span>
                  <span className="text-red-500">*</span>
                </label>
                <input
                  id="phonePrefix"
                  type="tel"
                  name="phonePrefix"
                  value={formData.phonePrefix}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full bg-white border border-gray-300 rounded-lg px-3 py-3 text-sm text-2c3e50 outline-none transition-all duration-200 focus:ring-1 focus:ring-EBC122 ${
                    errors.phonePrefix ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-EBC122'
                  }`}
                  placeholder={t('customer.phone.prefix.placeholder')}
                  maxLength="5"
                  required
                />
              </div>
              <div className="space-y-2 flex-1">
                <label className="flex items-center gap-2 text-sm font-medium text-2c3e50" htmlFor="phone">
                  <Phone size={16} className="text-6c757d" />
                  <span>{t('customer.phone.label')}</span>
                  <span className="text-red-500">*</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-sm text-2c3e50 outline-none transition-all duration-200 focus:ring-1 focus:ring-EBC122 ${
                    errors.phone ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-EBC122'
                  }`}
                  placeholder={t('customer.phone.placeholder')}
                  maxLength="9"
                  required
                />
              </div>
            </div>
            {(errors.phone || errors.phonePrefix) && (
              <div className="flex gap-2 items-center text-red-500 text-sm">
                <AlertCircle size={14} />
                <span>{errors.phone || errors.phonePrefix}</span>
              </div>
            )}
          </div>
        </div>

        {/* Dirección (obligatoria si total >= 3000€) */}
        {requiresAddress && (
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-2c3e50">
              <MapPin size={16} className="text-6c757d" />
              <span>{t('customer.address.label')}</span>
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-sm text-2c3e50 outline-none transition-all duration-200 focus:ring-1 focus:ring-EBC122 ${
                errors.address ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-EBC122'
              }`}
              placeholder={t('customer.address.placeholder')}
              required={requiresAddress}
            />
            {errors.address && (
              <div className="flex gap-2 items-center text-red-500 text-sm">
                <AlertCircle size={14} />
                <span>{errors.address}</span>
              </div>
            )}
          </div>
        )}

        {/* Documento de identificación (DNI, NIE, pasaporte) */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-2c3e50">
            <CreditCard size={16} className="text-6c757d" />
            <span>{t('customer.dni.label')}</span>
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="dni"
            value={formData.dni}
            onChange={handleChange}
            onBlur={handleBlur}
                className={`w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-sm text-2c3e50 outline-none transition-all duration-200 focus:ring-1 focus:ring-EBC122 uppercase ${
                errors.dni ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-EBC122'
            }`}
            placeholder={t('customer.dni.placeholder')}
            maxLength={20}
            required
          />
          {errors.dni && (
            <div className="flex gap-2 items-center text-red-500 text-sm">
              <AlertCircle size={14} />
              <span>{errors.dni}</span>
            </div>
          )}
        </div>

        {/* Entrepierna (opcional) para modelos compatibles */}
        {eligibleInseamVehicles.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="text-base sm:text-lg font-semibold text-2c3e50">
                {t('inseam.title')}
              </h3>
              <div className="relative" ref={inseamHelpWrapRef}>
                <button
                  type="button"
                  aria-label={t('inseam.help.aria')}
                  aria-expanded={showInseamHelp}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-primary bg-primary text-primary-dark hover:bg-d4a91e transition-colors shadow-sm"
                  onClick={() => setShowInseamHelp((v) => !v)}
                >
                  <Info size={18} aria-hidden />
                </button>
                {showInseamHelp && (
                  <div
                    role="dialog"
                    aria-label={t('inseam.help.aria')}
                    className="absolute right-0 mt-2 w-[260px] sm:w-[320px] rounded-xl border border-gray-200 bg-white shadow-lg p-3 z-20"
                  >
                    <img
                      src={inseamHelpImage}
                      alt={t('inseam.help.aria')}
                      className="w-full h-auto rounded-lg border border-gray-100 bg-white"
                    />
                    <p className="mt-2 text-xs text-6c757d">
                      {t('inseam.help.caption')}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <p className="text-sm text-6c757d mb-4">
              {t('inseam.subtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {eligibleInseamVehicles.map((v) => (
                <div key={v.id} className="space-y-2">
                  <label className="text-sm font-medium text-2c3e50">
                    {t('inseam.label')} ({v.model} · {v.size})
                  </label>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={30}
                    max={120}
                    step={0.5}
                    value={inseamByVehicleId?.[v.id] ?? ''}
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === '+') e.preventDefault();
                    }}
                    onChange={(e) => {
                      const nextRaw = e.target.value;
                      // Evitar valores negativos aunque el navegador deje escribir '-'
                      if (typeof nextRaw === 'string' && nextRaw.trim().startsWith('-')) {
                        return;
                      }
                      setInseamByVehicleId((prev) => ({
                        ...(prev || {}),
                        [v.id]: nextRaw,
                      }));
                    }}
                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-sm text-2c3e50 outline-none transition-all duration-200 focus:ring-1 focus:ring-EBC122 border-gray-300 focus:border-EBC122"
                    placeholder={t('inseam.placeholder')}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Información adicional */}
        <div className="bg-EBC122/10 border border-EBC122/20 rounded-xl p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <CheckCircle size={20} className="text-EBC122 mt-0.5" />
            <div>
              <h4 className="font-semibold text-2c3e50 mb-2">{t('customer.info.title')}</h4>
              <ul className="text-sm text-6c757d space-y-1">
                <li>{t('customer.info.protected')}</li>
                <li>{t('customer.info.dni.required')}</li>
                <li>{t('customer.info.email.confirmation')}</li>
                <li>{t('customer.info.contact')}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Botón continuar */}
        <div className="flex justify-end pt-3 sm:pt-4">
          <button
            type="submit"
            className="w-full sm:w-auto px-6 sm:px-8 py-4 bg-primary text-2c3e50 font-semibold rounded-lg hover:bg-d4a91e transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2 justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-2c3e50"></div>
                <span>{t('customer.processing')}</span>
              </div>
            ) : (
              t('common.continue')
            )}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default CustomerInfo; 